import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_USER_PROFILE } from '../../graphql';
import { apiUrl } from '../../lib/apiFetch';
import { showInkToast } from './InkToast';
import { NewSiteModal } from './NewSiteModal';
import { useSiteWaiting } from '../approvals/ApprovalsReview';
import { SiteTile } from './SiteSwitcher';
import {
  cardRoleLabel,
  planLabel,
  publishedLine,
  SiteSummary,
  useTinycmsSites,
  visibilityStamp,
} from './sites';

const DAY = 86400000;

/** Enter or Space does what a click does (a role="button" div). */
const enterOrSpace = (fn: () => void) => (e: React.KeyboardEvent) => {
  if (e.target !== e.currentTarget) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fn();
  }
};

/**
 * The site's screenshot (All Sites Thumbnails spec; Gallery cards, design
 * 2026-10-31): the live homepage filling a 16:10 frame, cropped from the
 * bottom, with the phone view rising from its bottom right, and nothing else
 * over it but the Current/New badge and the Password protected/Offline stamp. Before the first screenshot (or if it won't load),
 * the site's name on grey, and "Coming soon" when nothing is live yet; never a
 * spinner. A switched-off site's screenshot is faded.
 */
function SitePreview({ site, here, fresh }: { site: SiteSummary; here: boolean; fresh: boolean }) {
  const [broken, setBroken] = useState(false);
  const [phoneBroken, setPhoneBroken] = useState(false);
  const stamp = visibilityStamp(site.visibility);
  const shot = site.thumbnailUrl && !broken ? apiUrl(site.thumbnailUrl) : null;
  // The phone rises from the desktop shot, so only with one; never an empty bezel.
  const phone = shot && site.phoneUrl && !phoneBroken ? apiUrl(site.phoneUrl) : null;
  return (
    <div
      className="sp-shot"
      aria-hidden="true"
      data-offline={site.visibility === 'offline' ? 'true' : undefined}
    >
      <div className="sp-shot-page">
        {shot ? (
          <img src={shot} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />
        ) : (
          <div className="sp-shot-blank">
            <span className="sp-shot-name">{site.name}</span>
            {!site.lastPublishedAt ? <span className="sp-shot-soon">Coming soon</span> : null}
          </div>
        )}
      </div>
      {phone ? (
        <div className="sp-phone">
          <div className="sp-phone-screen">
            <img
              src={phone}
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setPhoneBroken(true)}
            />
          </div>
        </div>
      ) : null}
      {here ? (
        <span className="sp-badge">
          <span className="sp-badge-dot" />
          Current
        </span>
      ) : fresh ? (
        <span className="sp-badge sp-badge-new">New</span>
      ) : null}
      {stamp ? (
        <span className="sp-stamp" data-kind={site.visibility}>
          {site.visibility === 'password' ? <LockIcon /> : null}
          {stamp}
        </span>
      ) : null}
    </div>
  );
}

const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M5 7V5a3 3 0 1 1 6 0v2h.5A1.5 1.5 0 0 1 13 8.5v5a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 13.5v-5A1.5 1.5 0 0 1 4.5 7H5zm1.5 0h3V5a1.5 1.5 0 1 0-3 0v2z"
    />
  </svg>
);

/**
 * All sites ("tinycms UX optimization review v6.zip", Prototype v3; Gallery
 * cards 2026-10-31): each site is its screenshot with a two-line caption (no
 * card box), plus New site. The address in the caption opens the live site.
 * ?new=1 opens New site (the header's site menu links here that way).
 */
export const CmsSites: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { selectedAccount, selectAccountById } = useAccount();
  const waitingOf = useSiteWaiting();
  const { sites, loading } = useTinycmsSites();
  const { data: me } = useQuery<{ me?: { email?: string } }>(GET_USER_PROFILE);
  const creating = params.get('new') === '1';

  const openNew = () => setParams({ new: '1' });
  const closeNew = () => {
    params.delete('new');
    setParams(params, { replace: true });
  };

  const pick = (site: SiteSummary) => {
    if (site.accountId !== selectedAccount?.id) {
      selectAccountById(site.accountId);
      showInkToast(`Switched to ${site.name}.`);
    }
    navigate('/today');
  };

  return (
    <main className="ink-body sp">
      <div className="sp-head">
        <div>
          <h1 className="sp-title">Sites you manage</h1>
          {me?.me?.email ? <div className="sp-sub">{me.me.email}</div> : null}
        </div>
        <button type="button" className="ink-btn ink-btn-primary sp-new" onClick={openNew}>
          New site
        </button>
      </div>

      <div className="sp-grid" aria-busy={loading && !sites.length ? true : undefined}>
        {/* Grey shapes until the list is in (QA round 7: an empty grid read as
            "no sites" for seconds): a 16:10 block and two bars, no box. */}
        {loading && !sites.length ? (
          <span className="sr-only" role="status">
            Loading your sites
          </span>
        ) : null}
        {loading && !sites.length
          ? [0, 1, 2].map((i) => (
              <div key={i} className="sp-item sp-item-skel" aria-hidden="true">
                <span className="sp-shot sp-shot-skel" />
                <div className="sp-cap">
                  <span className="ink-skel-bar" data-size="lg" style={{ width: '55%' }} />
                  <span className="ink-skel-bar" style={{ width: '70%' }} />
                </div>
              </div>
            ))
          : null}
        {sites.map((site) => {
          const here = site.accountId === selectedAccount?.id;
          const waiting = waitingOf(site);
          const fresh = !here && Date.now() - new Date(site.createdAt).getTime() < DAY;
          // "Pro plan", "Free plan · Editor": the role only when it isn't Owner.
          const meta = [
            planLabel(site.plan) &&
              `${planLabel(site.plan)}${site.status !== 'active' ? ` (${site.status})` : ''}`,
            cardRoleLabel(site.role),
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div
              key={site.accountId}
              className="sp-item"
              data-here={here ? 'true' : undefined}
              role="button"
              tabIndex={0}
              aria-label={`Open ${site.name}`}
              onClick={() => pick(site)}
              onKeyDown={enterOrSpace(() => pick(site))}
            >
              <SitePreview site={site} here={here} fresh={fresh} />
              <div className="sp-cap">
                <div className="sp-cap-row">
                  <SiteTile site={site} size={22} plain />
                  <span className="sp-name">{site.name}</span>
                  <span className="sp-status" data-tone={waiting ? 'amber' : 'green'}>
                    <span className="sp-dot" />
                    {waiting ? `${waiting} to approve` : 'All caught up'}
                  </span>
                </div>
                <div className="sp-cap-row sp-cap-sub">
                  {/* The address opens the live site, never the site in the app. */}
                  {site.siteUrl ? (
                    <a
                      className="sp-host"
                      href={site.siteUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${site.host} in a new tab`}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {site.host} ↗
                    </a>
                  ) : (
                    <span className="sp-host">No address yet</span>
                  )}
                  {meta ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="sp-plan">{meta}</span>
                    </>
                  ) : null}
                  <span className="sp-last">{publishedLine(site.lastPublishedAt)}</span>
                </div>
              </div>
            </div>
          );
        })}

        {!loading || sites.length ? (
          <button type="button" className="sp-add" onClick={openNew}>
            <span className="sp-add-shot" aria-hidden="true">
              <span className="sp-add-plus">+</span>
            </span>
            <span className="sp-cap">
              <span className="sp-name">New site</span>
              <span className="sp-add-sub">
                Start another site. Paid plans start billing the day you add it.
              </span>
            </span>
          </button>
        ) : null}
      </div>

      {creating ? <NewSiteModal sites={sites} onClose={closeNew} /> : null}
    </main>
  );
};
